import React, { InputHTMLAttributes } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helpText?: string;
}

const Input: React.FC<InputProps> = ({
  label,
  error,
  helpText,
  className = '',
  id,
  ...props
}) => {
  const inputId = id || `input-${label?.replace(/\s+/g, '-').toLowerCase()}`;
  const errorId = `${inputId}-error`;
  const helpTextId = `${inputId}-help`;

  // Build aria-describedby based on what's visible
  const describedByParts: string[] = [];
  if (error) describedByParts.push(errorId);
  if (helpText && !error) describedByParts.push(helpTextId);
  const describedBy = describedByParts.length > 0 ? describedByParts.join(' ') : undefined;

  // Build aria props conditionally
  const ariaProps: Record<string, string> = {};
  if (error) ariaProps['aria-invalid'] = 'true';
  if (describedBy) ariaProps['aria-describedby'] = describedBy;

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label}
          {props.required && <span className="text-red-500 ml-1" aria-hidden="true">*</span>}
        </label>
      )}
      <input
        id={inputId}
        className={`w-full px-3 py-2 border rounded-lg shadow-xs focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
          error ? 'border-red-500' : 'border-gray-300'
        } ${className}`}
        {...ariaProps}
        {...props}
      />
      {helpText && !error && (
        <p id={helpTextId} className="mt-1 text-sm text-gray-500">{helpText}</p>
      )}
      {error && (
        <p id={errorId} className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default Input;
